/**
 * The bubble's "More" menu — the actions that do not earn an icon in the bar.
 *
 * It takes one flat list — whatever the bar could not fit, in the bar's own priority order
 * — and draws the destructive items last under a rule, so a fast pointer never lands on
 * Delete. That partition is the menu's, not the caller's: the caller orders by priority and
 * does not know where the overflow will fall.
 *
 * The trigger's `aria-expanded` is what keeps the action bar visible while the menu is up,
 * and it resolves because this trigger stays inside the bar even though the content is
 * portaled out of it. Neither of the other two attributes can do that job here:
 * `MESSAGE_HOVER_VISIBLE` matches a tooltip's `delayed-open` / `instant-open` and never a
 * plain `open`, and `data-state` never reads `open` on this button at all — `TooltipTrigger`
 * wraps `DropdownMenuTrigger` `asChild`, and the outer trigger's props are spread last, so
 * the tooltip's `closed` overwrites the menu's `open`. See `channel-message-action-bar`.
 *
 * Picking an item CLOSES the menu and then retires the bar through `onSelectItem`: the
 * action the reader came for has run, and the bar has nothing left to offer until they leave
 * the message and come back. Radix's own focus restore is suppressed on the content — it
 * would hand focus back to this trigger, which by then sits in a bar that is `invisible` and
 * therefore unfocusable, and it would take focus away from whatever the action opened.
 *
 * No tooltip on a menu row: see `components/ui/common/copy.tsx` for why the two do not mix.
 */

import { cn } from "cn";
import { kebabCase } from "lodash-es";

import { Button } from "@/components/ui/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BwMoreFill } from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ChannelMessageMenuItem } from "../_types/channel-surface";

type ChannelMessageMoreMenuProps = {
  /** Everything the bar could not fit, in its priority order. */
  items: ChannelMessageMenuItem[];
  /** Worn by the trigger, so it matches the bar's other buttons. */
  className?: string;
  /** Called after an item has run, so the bar holding this trigger can retire itself. */
  onSelectItem: () => void;
};

export function ChannelMessageMoreMenu({
  items,
  className,
  onSelectItem,
}: ChannelMessageMoreMenuProps) {
  if (items.length === 0) return null;

  const groups = [
    items.filter((item) => item.variant !== "destructive"),
    items.filter((item) => item.variant === "destructive"),
  ].filter((group) => group.length > 0);

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              kind="ghost"
              variant="neutral"
              // The bar's own size — this trigger is one of its buttons, and the only
              // place the menu is opened from.
              size="icon-md"
              aria-label="More actions"
              data-test="message-more-actions"
              className={className}
            >
              <BwMoreFill />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>More actions</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        // The bar is right-aligned against the bubble, so the menu opens off its own right
        // edge and stays inside the thread column.
        align="end"
        data-test="message-more-menu"
        // Figma's dropdown is a wider, rounder card than the app default, and it pads each
        // group rather than the content — which is what puts the rule between two groups
        // flush with the card's edges.
        className="w-60 rounded-3xl p-0"
        // Radix hands focus back to the trigger when the menu closes. The trigger is in the
        // bar that the select just retired, so the browser would drop that focus to the
        // body a frame later — and on the way it would take focus from whatever the action
        // opened, a thread panel's composer among them.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {groups.map((group, index) => (
          <div
            key={group.map((item) => item.title).join("|")}
            className={cn(
              /* sorted */
              "p-2",
              index > 0 && "border-t border-border-dark-min",
            )}
          >
            {group.map((item) => (
              <DropdownMenuItem
                key={kebabCase(item.title)}
                data-test={kebabCase(item.title)}
                variant={item.variant ?? "default"}
                className="h-8 rounded-2xl [&_svg:not([class*='size-'])]:size-5"
                onSelect={(event) => {
                  item.onClick(event);
                  onSelectItem();
                }}
              >
                <item.icon />
                {item.title}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
